<template>
  <div>
    <div class="filter-tools">
      <el-radio-group
        v-model="searchMode"
        class="search-mode-switch"
        @change="handleSearchModeChange"
      >
        <el-radio-button label="event">按赛事ID/关键词</el-radio-button>
        <el-radio-button label="person">按姓名/手机号</el-radio-button>
      </el-radio-group>
      <el-select
        v-if="searchMode === 'event'"
        ref="eventSearchSelect"
        v-model="query"
        class="event-search-select"
        popper-class="event-search-popper"
        filterable
        remote
        clearable
        reserve-keyword
        :remote-method="searchEventOptions"
        :loading="eventSearchLoading"
        placeholder="输入赛事ID或关键词，多个关键词用空格、逗号分隔"
        @change="handleEventSelectionChange"
        @clear="handleEventSelectionClear"
        @visible-change="handleEventDropdownVisible"
      >
        <el-option
          v-for="event in eventOptions"
          :key="event._id"
          :label="event.name"
          :value="event._id"
        >
          <div class="event-option">
          <span class="event-option-name">{{ event.name }}</span>
          <span class="event-option-meta">
            {{ formatEventOptionMeta(event) }}
          </span>
        </div>
      </el-option>
        <el-option
          v-if="eventSearchLoadingMore"
          key="__event_loading_more__"
          label="加载更多赛事..."
          value="__event_loading_more__"
          disabled
        >
          <div class="event-option-loading">加载更多赛事...</div>
        </el-option>
      </el-select>
      <el-input
        v-else
        v-model.trim="personQuery"
        class="person-search-input"
        clearable
        maxlength="50"
        prefix-icon="el-icon-user"
        placeholder="输入运动员姓名或手机号"
        @clear="handlePersonSearchClear"
        @keyup.enter.native="searchRegistrations(true)"
      ></el-input>
      <el-button
        v-if="searchMode === 'event'"
        class="search-submit-button"
        icon="el-icon-search"
        type="primary"
        :disabled="!query"
        @click="handleQuery"
      >查询赛事</el-button>
      <el-button
        v-else
        class="search-submit-button"
        type="primary"
        icon="el-icon-search"
        :loading="personSearchLoading"
        :disabled="!personQuery"
        @click="searchRegistrations(true)"
      >查询人员</el-button>
      <el-button
        v-if="searchMode === 'person' && personQuery"
        icon="el-icon-refresh-left"
        @click="handlePersonSearchClear"
      >重置</el-button>
      <div class="filter-switch">
        <span class="switch-label">只看支付成功</span>
        <el-switch
          v-model="onlyPaid"
          @change="handleOnlyPaidChange"
        ></el-switch>
      </div>
      <el-tooltip
        v-if="canExportRegistration && searchMode === 'event'"
        :disabled="canExportCurrentQuery"
        :content="exportDisabledTip"
        placement="top"
      >
        <span class="export-button-wrap">
          <el-button
            class="btn-export"
            icon="el-icon-download"
            type="primary"
            :loading="exporting"
            :disabled="!canExportCurrentQuery"
            @click="exportExcel"
          >导出</el-button>
        </span>
      </el-tooltip>
      <div v-if="paidCount !== null" class="paid-count">
        <el-tag type="success" effect="dark">报名成功人数：{{ paidCount }}</el-tag>
      </div>
    </div>
    <div v-if="tableData.length" class="page-locator">
      <el-input
        ref="locatorInput"
        v-model="locatorKeyword"
        class="locator-input"
        size="small"
        clearable
        prefix-icon="el-icon-search"
        placeholder="页内定位：输入姓名、手机号、订单号等"
        @input="handleLocatorInput"
        @clear="clearLocator"
        @keyup.enter.native="goNextLocateMatch"
      ></el-input>
      <div class="locator-actions">
        <el-button
          icon="el-icon-arrow-up"
          size="small"
          :disabled="!locatorMatches.length"
          @click="goPrevLocateMatch"
        ></el-button>
        <span class="locator-count">{{ locatorCountText }}</span>
        <el-button
          icon="el-icon-arrow-down"
          size="small"
          :disabled="!locatorMatches.length"
          @click="goNextLocateMatch"
        ></el-button>
      </div>
    </div>
    <el-table
      v-loading="personSearchLoading"
      ref="formResultTable"
      :data="tableData"
      :row-class-name="getTableRowClassName"
      style="width: 100%"
    >
      <el-table-column prop="_id" label="ID" width="170">
        <template slot-scope="scope">
          <copy-text :value="scope.row._id"></copy-text>
        </template>
      </el-table-column>
      <el-table-column prop="eventId" label="赛事ID" width="170">
        <template slot-scope="scope">
          <copy-text :value="scope.row.eventId"></copy-text>
        </template>
      </el-table-column>
      <el-table-column prop="eventName" label="比赛名称" width="330">
        <template slot-scope="scope">
          <copy-text :value="scope.row.eventName"></copy-text>
        </template>
      </el-table-column>
      <el-table-column prop="createdAt" label="报名时间" width="200">
        <template slot-scope="scope">
          {{ $dayjs(scope.row.createdAt).format("YYYY-MM-DD HH:mm:ss") }}
        </template>
      </el-table-column>
      <el-table-column prop="name" label="运动员姓名" width="100"></el-table-column>
      <el-table-column prop="sex" label="性别" width="50">
        <template slot-scope="scope">
          <span v-if="scope.row.sex" class="color-male">♂</span>
          <span v-else class="color-female">♀</span>
        </template>
      </el-table-column>
      <el-table-column prop="phone" label="手机号" width="100"></el-table-column>
      <el-table-column prop="idType" label="证件类型" width="120">
        <template slot-scope="scope">
          <span>{{ getIdTypeName(scope.row.idType) }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="wechat" label="微信号" width="100"></el-table-column>
      <el-table-column prop="weMediaId" label="自媒体号" width="100"></el-table-column>
      <el-table-column prop="weMediaFans" label="自媒体全网粉丝数" width="100"></el-table-column>
      <el-table-column prop="items" label="报名项目" width="100">
        <template slot-scope="scope">
          <div v-for="(item, index) in scope.row.items" :key="index">{{ item.item + item.group }}</div>
        </template>
      </el-table-column>
      <el-table-column prop="orderNo" label="报名订单号" width="120"></el-table-column>
      <el-table-column label="飞书通知" width="160" align="center" header-align="center">
        <template slot-scope="scope">
          <div class="lark-notification-cell">
            <div class="lark-status-row">
              <span
                class="lark-status-pill"
                :class="`is-${scope.row.larkNotificationStatus || 'untracked'}`"
              >
                <i :class="getLarkNotificationStatusIcon(scope.row.larkNotificationStatus)"></i>
                {{ getLarkNotificationStatusText(scope.row.larkNotificationStatus) }}
              </span>
              <span
                v-if="scope.row.larkReturningAthlete === true"
                class="lark-returning-badge"
              >老用户</span>
            </div>
            <div
              v-if="scope.row.larkNotificationSentAt || shouldShowLarkAttempts(scope.row)"
              class="lark-notification-meta"
            >
              <span v-if="scope.row.larkNotificationSentAt">
                {{ $dayjs(scope.row.larkNotificationSentAt).format("MM-DD HH:mm") }}
              </span>
              <span v-if="scope.row.larkNotificationSentAt && shouldShowLarkAttempts(scope.row)" class="lark-meta-dot">·</span>
              <span v-if="shouldShowLarkAttempts(scope.row)">尝试 {{ scope.row.larkNotificationAttempts }} 次</span>
              <el-tooltip
                v-if="scope.row.larkNotificationLastError"
                :content="scope.row.larkNotificationLastError"
                placement="top"
              >
                <i class="el-icon-warning-outline lark-error-icon" aria-label="查看最近错误"></i>
              </el-tooltip>
            </div>
            <el-button
              v-if="canRetryLarkNotification(scope.row)"
              class="lark-retry-button"
              type="primary"
              plain
              round
              icon="el-icon-refresh"
              size="mini"
              :loading="retryingLarkNotificationId === scope.row._id"
              @click="retryLarkNotification(scope.row)"
            >{{ scope.row.larkNotificationStatus === 'untracked' ? '补发通知' : '立即重试' }}</el-button>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="追加兼项" width="100">
        <template slot-scope="scope">
          <el-button type="text" size="small" @click="showAddonOrders(scope.row)">
            {{ scope.row.addonPaymentAmount ? `¥${divide(scope.row.addonPaymentAmount, 100)}` : '查看' }}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column prop="enableCourse" label="是否报名课程" width="120">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.enableCourse" type="success" effect="dark" size="mini">是</el-tag>
          <el-tag v-else type="danger" effect="dark" size="mini">否</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="school" label="学校" width="120"></el-table-column>
      <el-table-column prop="grade" label="年级" width="120"></el-table-column>
      <el-table-column prop="age" label="年龄" width="50"></el-table-column>
      <el-table-column prop="isPaid" label="是否已支付" width="50">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.isPaid" type="success" effect="dark" size="mini">是</el-tag>
          <el-tag v-else type="danger" effect="dark" size="mini">否</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="payableAmount" label="应付金额" width="50">
        <template slot-scope="scope">
          {{ divide(scope.row.payableAmount, 100) }}
        </template>
      </el-table-column>
      <el-table-column prop="paymentAmount" label="支付金额" width="50">
        <template slot-scope="scope">
          {{ divide(scope.row.paymentAmount, 100) }}
        </template>
      </el-table-column>
      <el-table-column label="支付总金额" width="100">
        <template slot-scope="scope">
          {{ divide(getTotalPaymentAmount(scope.row), 100) }}
        </template>
      </el-table-column>
      <el-table-column prop="couponCode" label="优惠码" width="120">
        <template slot-scope="scope">
          <span v-if="scope.row.couponCode">{{ scope.row.couponCode }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="couponName" label="优惠券名称" width="130">
        <template slot-scope="scope">
          <span v-if="scope.row.couponName">{{ scope.row.couponName }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="originalPayableAmount" label="优惠前金额" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.couponCode">{{ divide(scope.row.originalPayableAmount, 100) }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="couponDiscountAmount" label="优惠金额" width="100">
        <template slot-scope="scope">
          <span v-if="scope.row.couponCode">{{ divide(scope.row.couponDiscountAmount, 100) }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="couponUsedAt" label="优惠码使用时间" width="180">
        <template slot-scope="scope">
          <span v-if="scope.row.couponUsedAt">{{ $dayjs(scope.row.couponUsedAt).format("YYYY-MM-DD HH:mm:ss") }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="paymentMode" label="支付模式" width="120">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.paymentMode === 'platform'" type="info" size="mini">平台代收</el-tag>
          <el-tag v-else-if="scope.row.paymentMode === 'service_provider'" type="primary" size="mini">服务商分账</el-tag>
          <el-tag v-else type="warning" size="mini">未知</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="subMchId" label="特约商户号" width="150">
        <template slot-scope="scope">
          <span v-if="scope.row.subMchId">{{ scope.row.subMchId }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="transactionId" label="微信支付订单号" width="200">
        <template slot-scope="scope">
          <span v-if="scope.row.transactionId">{{ scope.row.transactionId }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="isProfitSharing" label="是否分账" width="90">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.isProfitSharing" type="success" effect="dark" size="mini">是</el-tag>
          <el-tag v-else type="info" effect="dark" size="mini">否</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="profitSharingStatus" label="分账状态" width="100">
        <template slot-scope="scope">
          <el-tag v-if="scope.row.profitSharingStatus === 'none'" type="info" size="mini">无需分账</el-tag>
          <el-tag v-else-if="scope.row.profitSharingStatus === 'pending'" type="warning" size="mini">待分账</el-tag>
          <el-tag v-else-if="scope.row.profitSharingStatus === 'submitted'" type="warning" size="mini">已受理</el-tag>
          <el-tag v-else-if="scope.row.profitSharingStatus === 'success'" type="success" size="mini">成功</el-tag>
          <el-tag v-else-if="scope.row.profitSharingStatus === 'failed'" type="danger" size="mini">失败</el-tag>
          <el-tag v-else type="info" size="mini">未知</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="mainProfitSharingAmount" label="主订单分润金额" width="130">
        <template slot-scope="scope">
          <span>{{ divide(scope.row.mainProfitSharingAmount || scope.row.platformFee || 0, 100) }} 元</span>
        </template>
      </el-table-column>
      <el-table-column prop="addonProfitSharingAmount" label="兼项订单分润金额" width="140">
        <template slot-scope="scope">
          <span>{{ divide(scope.row.addonProfitSharingAmount || 0, 100) }} 元</span>
        </template>
      </el-table-column>
      <el-table-column prop="totalProfitSharingAmount" label="订单分润总金额" width="130">
        <template slot-scope="scope">
          <span>{{ divide(scope.row.totalProfitSharingAmount || scope.row.platformFee || 0, 100) }} 元</span>
        </template>
      </el-table-column>
      <el-table-column prop="profitSharingReturnStatus" label="回退状态" width="100">
        <template slot-scope="scope">
          <el-tag :type="getProfitSharingReturnStatusType(scope.row.profitSharingReturnStatus)" size="mini">
            {{ getProfitSharingReturnStatusText(scope.row.profitSharingReturnStatus) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="profitSharingReturnedAmount" label="已回退金额" width="120">
        <template slot-scope="scope">
          <span v-if="scope.row.profitSharingReturnedAmount">{{ divide(scope.row.profitSharingReturnedAmount, 100) }} 元</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="profitSharingNo" label="分账单号" width="200">
        <template slot-scope="scope">
          <span v-if="scope.row.profitSharingNo">{{ scope.row.profitSharingNo }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column label="分账响应" width="100">
        <template slot-scope="scope">
          <el-button
            v-if="scope.row.profitSharingResult"
            type="text"
            size="small"
            @click="showProfitSharingResult(scope.row.profitSharingResult)"
          >查看详情</el-button>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column label="回退响应" width="100">
        <template slot-scope="scope">
          <el-button
            v-if="scope.row.profitSharingReturnResult"
            type="text"
            size="small"
            @click="showProfitSharingResult(scope.row.profitSharingReturnResult)"
          >查看详情</el-button>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="管理员备注" width="200">
        <template slot-scope="scope">
          <span v-if="scope.row.remark">{{ scope.row.remark }}</span>
          <span v-else style="color: #909399;">-</span>
        </template>
      </el-table-column>
      <el-table-column label="比赛时间" width="180">
        <template slot-scope="scope">
          <span v-if="scope.row.fuzzyTime">{{ scope.row.fuzzyTime }}</span>
          <span>
            {{ $dayjs(scope.row.eventStartTime).format("YYYY-MM-DD") }} -
            {{ $dayjs(scope.row.eventEndTime).format("YYYY-MM-DD") }}
          </span>
        </template>
      </el-table-column>
      <el-table-column v-if="hasOperationActions" label="操作" :width="operationColumnWidth" fixed="right">
        <template slot-scope="scope">
          <div class="form-result-actions">
            <el-button
              v-if="canReturnProfitSharing(scope.row)"
              type="warning"
              icon="el-icon-refresh-left"
              size="small"
              :title="scope.row.profitSharingReturnStatus === 'processing' ? '重试回退' : '退回分账'"
              :loading="returningOrderNo === scope.row.orderNo"
              @click="handleProfitSharingReturn(scope.row)"
            >
              <span class="profit-return-text">
                {{ scope.row.profitSharingReturnStatus === 'processing' ? '重试回退' : '退回分账' }}
              </span>
            </el-button>
            <el-button
              v-if="canUpdateRegistration"
              type="primary"
              icon="el-icon-edit"
              size="small"
              title="编辑"
              @click="handleEdit(scope.row)"
            ></el-button>
            <el-button
              v-if="canDeleteRegistration"
              type="danger"
              icon="el-icon-delete"
              size="small"
              title="删除"
              @click="handleDelete(scope.row._id)"
            ></el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="searchMode === 'person' ? total > pageSize : !query"
      class="pager"
      background
      layout="prev, pager, next"
      :total="total"
      :page-size="pageSize"
      :current-page.sync="pageNum"
      @current-change="onCurrentPageChange"
    >
    </el-pagination>

    <!-- 分账响应详情弹窗 -->
    <el-dialog
      title="分账响应详情"
      :visible.sync="profitSharingDialogVisible"
      width="60%"
    >
      <pre class="json-viewer">{{ profitSharingResultText }}</pre>
      <span slot="footer" class="dialog-footer">
        <el-button @click="profitSharingDialogVisible = false">关闭</el-button>
        <el-button type="primary" @click="copyToClipboard">复制</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="追加兼项记录"
      :visible.sync="addonDialogVisible"
      width="92%"
      custom-class="mobile-form-dialog"
    >
      <div class="addon-dialog-toolbar">
        <span>{{ addonBaseOrder.eventName || '' }} {{ addonBaseOrder.name ? `· ${addonBaseOrder.name}` : '' }}</span>
        <el-button
          v-if="canExportRegistration"
          class="addon-export-button"
          size="small"
          type="primary"
          plain
          icon="el-icon-download"
          :disabled="addonLoading || !addonOrders.length"
          @click="exportAddonOrders"
        >导出追加单</el-button>
      </div>
      <el-table v-loading="addonLoading" :data="addonOrders" border max-height="560">
        <el-table-column prop="businessOrderNo" label="追加单号" width="230"></el-table-column>
        <el-table-column prop="transactionId" label="微信交易号" width="260">
          <template slot-scope="scope">{{ scope.row.transactionId || '--' }}</template>
        </el-table-column>
        <el-table-column label="追加项目" min-width="220">
          <template slot-scope="scope">
            <div v-for="(item, index) in scope.row.items || []" :key="index">
              {{ item.item }}{{ item.group }}
            </div>
          </template>
        </el-table-column>
        <el-table-column label="金额" width="90">
          <template slot-scope="scope">{{ divide(scope.row.paymentAmount || scope.row.payableAmount, 100) }} 元</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="scope">{{ getAddonStatusText(scope.row.status) }}</template>
        </el-table-column>
        <el-table-column label="分账" width="110">
          <template slot-scope="scope">
            {{ scope.row.isProfitSharing ? getProfitSharingStatusText(scope.row.profitSharingStatus) : '无需分账' }}
          </template>
        </el-table-column>
        <el-table-column label="平台分账金额" width="130">
          <template slot-scope="scope">{{ divide(scope.row.platformFee || 0, 100) }} 元</template>
        </el-table-column>
        <el-table-column prop="profitSharingNo" label="分账单号" width="230">
          <template slot-scope="scope">{{ scope.row.profitSharingNo || '--' }}</template>
        </el-table-column>
        <el-table-column label="分账响应" width="100" align="center">
          <template slot-scope="scope">
            <el-button
              type="text"
              size="small"
              :disabled="!scope.row.profitSharingResult"
              @click="showProfitSharingResult(scope.row.profitSharingResult)"
            >查看</el-button>
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="170">
          <template slot-scope="scope">{{ $dayjs(scope.row.createdAt).format("YYYY-MM-DD HH:mm:ss") }}</template>
        </el-table-column>
        <el-table-column
          v-if="canReturnProfitSharingPermission"
          label="操作"
          width="110"
          fixed="right"
          align="center"
        >
          <template slot-scope="scope">
            <el-button
              type="text"
              size="small"
              :loading="retryingAddonOrderNo === scope.row.businessOrderNo"
              :disabled="!canRetryAddonProfitSharing(scope.row)"
              @click="retryAddonProfitSharing(scope.row)"
            >重试分账</el-button>
          </template>
        </el-table-column>
      </el-table>
      <span slot="footer"><el-button @click="addonDialogVisible = false">关闭</el-button></span>
    </el-dialog>

    <!-- 修改报名记录弹窗 -->
    <el-dialog
      title="修改报名记录"
      :visible.sync="editDialogVisible"
      width="600px"
      custom-class="mobile-form-dialog"
      :close-on-click-modal="false"
    >
      <el-form
        ref="editForm"
        :model="editForm"
        :rules="editFormRules"
        label-width="100px"
      >
        <el-form-item label="姓名" prop="name">
          <el-input v-model="editForm.name" placeholder="请输入运动员姓名"></el-input>
        </el-form-item>

        <el-form-item label="手机号" prop="phone">
          <el-input v-model="editForm.phone" placeholder="请输入手机号" maxlength="11"></el-input>
        </el-form-item>

        <el-form-item label="微信号" prop="wechat">
          <el-input v-model="editForm.wechat" placeholder="请输入微信号"></el-input>
        </el-form-item>

        <el-form-item label="代表战队" prop="team">
          <el-input v-model="editForm.team" placeholder="请输入代表战队（可选）"></el-input>
        </el-form-item>

        <el-form-item label="报名项目" prop="items">
          <div v-for="(item, index) in editForm.items" :key="index" class="item-row">
            <el-input
              v-model="item.item"
              placeholder="项目名称"
              class="item-input"
            ></el-input>
            <el-input
              v-model="item.group"
              placeholder="组别"
              class="item-input"
            ></el-input>
            <el-input
              :value="item.price + ' 元'"
              placeholder="价格"
              class="item-price"
              disabled
            ></el-input>
            <el-button
              v-if="editForm.items.length > 1"
              type="danger"
              icon="el-icon-delete"
              size="small"
              @click="removeItem(index)"
            ></el-button>
          </div>
          <el-button
            type="primary"
            icon="el-icon-plus"
            size="small"
            plain
            @click="addItem"
          >添加项目</el-button>
        </el-form-item>

        <el-form-item label="管理员备注">
          <el-input
            type="textarea"
            v-model="editForm.remark"
            placeholder="请输入备注（修改场次、延期等操作说明）"
            maxlength="500"
            show-word-limit
            :rows="3"
          ></el-input>
        </el-form-item>

        <el-form-item label="是否分账">
          <el-switch
            v-model="editForm.isProfitSharing"
            active-text="是"
            inactive-text="否"
          ></el-switch>
        </el-form-item>

        <el-form-item label="分账状态">
          <el-select v-model="editForm.profitSharingStatus" placeholder="请选择分账状态">
            <el-option label="无需分账" value="none"></el-option>
            <el-option label="待分账" value="pending"></el-option>
            <el-option label="已受理" value="submitted"></el-option>
            <el-option label="成功" value="success"></el-option>
            <el-option label="失败" value="failed"></el-option>
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmitEdit" :loading="submitting">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import _ from "loadsh";
import { hasPermission } from "../../utils/auth";
import {
  exportRegistrationRows,
  exportAddonRows,
  formatBeijingDateCompact,
  getRegistrationExportFileName,
  sanitizeRegistrationFileName,
  saveAsExcelFile
} from '@/utils/registrationExport';

export default {
  data() {
    return {
      editDialogVisible: false,
      tableData: [],
      total: 0,
      pageNum: 1,
      pageSize: 10,
      formObj: {},
      searchMode: "event",
      query: "",
      personQuery: "",
      eventOptions: [],
      eventSearchLoading: false,
      eventSearchLoadingMore: false,
      eventSearchRequestId: 0,
      eventSearchKeyword: "",
      eventSearchPageNum: 1,
      eventSearchPageSize: 20,
      eventSearchTotal: 0,
      eventSearchScrollElement: null,
      onlyPaid: true,
      personSearchLoading: false,
      profitSharingDialogVisible: false,
      profitSharingResultText: "",
      addonDialogVisible: false,
      addonLoading: false,
      addonOrders: [],
      addonBaseOrder: {},
      retryingAddonOrderNo: '',
      retryingLarkNotificationId: '',
      editForm: {
        _id: '',
        name: '',
        phone: '',
        wechat: '',
        items: [{ item: '', group: '', price: 0 }],
        team: '',
        isProfitSharing: false,
        profitSharingStatus: 'none',
        remark: ''
      },
      editFormRules: {
        name: [
          { required: true, message: '请输入运动员姓名', trigger: 'blur' },
          { min: 1, max: 50, message: '姓名长度在 1 到 50 个字符', trigger: 'blur' }
        ],
        phone: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
        ],
        wechat: [
          { required: true, message: '请输入微信号', trigger: 'blur' },
          { min: 1, max: 50, message: '微信号长度在 1 到 50 个字符', trigger: 'blur' }
        ],
        items: [
          {
            type: 'array',
            required: true,
            message: '请至少添加一个报名项目',
            trigger: 'change'
          }
        ]
      },
      submitting: false,
      exporting: false,
      paidCount: null,
      returningOrderNo: '',
      isMobile: false,
      locatorKeyword: '',
      locatorMatches: [],
      locatorActiveIndex: -1,
      locatorActiveRowId: ''
    };
  },
  computed: {
    canUpdateRegistration() {
      return hasPermission('registration:update');
    },
    canManageLarkNotification() {
      return hasPermission('registration:larkNotification');
    },
    canDeleteRegistration() {
      return hasPermission('registration:delete');
    },
    canExportRegistration() {
      return hasPermission('registration:export');
    },
    canExportCurrentQuery() {
      return this.searchMode === 'event' &&
        Boolean(String(this.query || '').trim()) &&
        this.tableData.length > 0 &&
        !this.exporting;
    },
    exportDisabledTip() {
      if (this.exporting) return '正在导出，请稍候';
      if (!String(this.query || '').trim()) return '请先输入赛事ID并查询';
      if (!this.tableData.length) return '当前赛事暂无可导出的报名名单';
      return '';
    },
    canReturnProfitSharingPermission() {
      return hasPermission('registration:profitSharing');
    },
    hasOperationActions() {
      return this.canUpdateRegistration ||
        this.canDeleteRegistration ||
        this.canReturnProfitSharingPermission;
    },
    operationColumnWidth() {
      return this.isMobile ? 64 : 230;
    },
    locatorCountText() {
      if (!this.locatorKeyword.trim()) return '0/0';
      if (!this.locatorMatches.length) return '0/0';
      return `${this.locatorActiveIndex + 1}/${this.locatorMatches.length}`;
    }
  },
  created() {
    const eventId = this.$route.query.eventId;
    if (eventId) {
      this.query = eventId;
      this.searchEventOptions(eventId);
      this.handleQuery();
    } else {
      this.getData();
    }
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
    window.addEventListener('keydown', this.handleLocatorShortcut);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
    window.removeEventListener('keydown', this.handleLocatorShortcut);
    this.detachEventSearchScroll();
  },
  methods: {
    divide: _.divide,
    getTotalPaymentAmount(row) {
      return Number((row && row.paymentAmount) || 0) + Number((row && row.addonPaymentAmount) || 0);
    },
    getAddonStatusText(status) {
      return {
        pending_payment: '待支付',
        payment_processing: '处理中',
        paid: '已完成',
        voided: '已作废',
        expired: '已过期',
        payment_exception: '同步异常'
      }[status] || status || '未知';
    },
    getProfitSharingStatusText(status) {
      return {
        none: '无需分账',
        pending: '待分账',
        submitted: '已受理',
        success: '成功',
        failed: '失败'
      }[status] || status || '未知';
    },
    getLarkNotificationStatusText(status) {
      return {
        untracked: '未跟踪',
        pending: '待发送',
        sending: '发送中',
        retrying: '重试中',
        sent: '已发送'
      }[status] || '未跟踪';
    },
    getLarkNotificationStatusIcon(status) {
      return {
        untracked: 'el-icon-minus',
        pending: 'el-icon-time',
        sending: 'el-icon-loading',
        retrying: 'el-icon-refresh',
        sent: 'el-icon-circle-check'
      }[status] || 'el-icon-minus';
    },
    shouldShowLarkAttempts(row) {
      const attempts = Number((row && row.larkNotificationAttempts) || 0);
      return attempts > 1 || (attempts > 0 && row.larkNotificationStatus !== 'sent');
    },
    canRetryLarkNotification(row) {
      return Boolean(
        this.canManageLarkNotification &&
        row &&
        (row.isPaid || row.isFree) &&
        !['sent', 'pending', 'sending'].includes(row.larkNotificationStatus || 'untracked')
      );
    },
    retryLarkNotification(row) {
      if (!this.canRetryLarkNotification(row)) return;
      const actionText = (row.larkNotificationStatus || 'untracked') === 'untracked' ? '补发' : '立即重试';
      this.$confirm(
        `确认${actionText}${row.name || '该运动员'}的飞书报名通知？`,
        '飞书通知确认',
        {
          confirmButtonText: actionText,
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.retryingLarkNotificationId = row._id;
        return this.$axios.post('/form-result/retry_lark_notification', { id: row._id });
      }).then(() => {
        this.$message.success('已加入飞书通知队列');
        if (this.searchMode === 'person') {
          return this.searchRegistrations(false);
        }
        return this.getData();
      }).catch((err) => {
        if (err === 'cancel' || err === 'close') return;
        this.$message.error((err.response && err.response.data && err.response.data.message) || '飞书通知入队失败');
      }).finally(() => {
        this.retryingLarkNotificationId = '';
      });
    },
    showAddonOrders(row) {
      this.addonBaseOrder = row || {};
      this.addonDialogVisible = true;
      this.loadAddonOrders();
    },
    loadAddonOrders() {
      if (!this.addonBaseOrder._id) return;
      this.addonLoading = true;
      this.addonOrders = [];
      this.$axios.post('/form-result/find_addons', {
        baseRegistrationId: this.addonBaseOrder._id
      }, {
        headers: { 'x-auth-token': localStorage.getItem('bde_token') }
      }).then((res) => {
        this.addonOrders = (res.data && res.data.res) || [];
      }).catch((err) => {
        this.$message.error((err.response && err.response.data && err.response.data.message) || '追加记录加载失败');
      }).finally(() => {
        this.addonLoading = false;
      });
    },
    canRetryAddonProfitSharing(row) {
      return Boolean(
        this.canReturnProfitSharingPermission &&
        row &&
        row.status === 'paid' &&
        row.paymentMode === 'service_provider' &&
        row.isProfitSharing &&
        ['pending', 'failed', 'submitted'].includes(row.profitSharingStatus)
      );
    },
    retryAddonProfitSharing(row) {
      if (!this.canRetryAddonProfitSharing(row)) return;
      this.$confirm(
        `确认将追加单 ${row.businessOrderNo} 加入分账重试队列？`,
        '重试分账确认',
        {
          confirmButtonText: '确认重试',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.retryingAddonOrderNo = row.businessOrderNo;
        return this.$axios.post('/form-result/retry_addon_profit_sharing', {
          businessOrderNo: row.businessOrderNo
        }, {
          headers: { 'x-auth-token': localStorage.getItem('bde_token') }
        });
      }).then(() => {
        this.$message.success('已加入分账重试队列，定时任务将尽快处理');
        this.loadAddonOrders();
      }).catch((err) => {
        if (err === 'cancel' || err === 'close') return;
        this.$message.error((err.response && err.response.data && err.response.data.message) || '加入分账重试队列失败');
      }).finally(() => {
        this.retryingAddonOrderNo = '';
      });
    },
    exportAddonOrders() {
      if (!this.addonOrders.length) {
        this.$message.warning('暂无可导出的追加兼项订单');
        return;
      }
      exportAddonRows(this.addonOrders, this.addonBaseOrder, this.$dayjs);
    },
    formatEventOptionMeta(event) {
      const time = event.fuzzyTime || (
        event.startTime
          ? this.$dayjs(event.startTime).format('YYYY-MM-DD')
          : '时间待定'
      );
      return [time, event.address].filter(Boolean).join(' · ');
    },
    searchEventOptions(keyword) {
      const normalizedKeyword = String(keyword || '').trim();
      this.eventSearchRequestId += 1;
      this.eventSearchLoading = false;
      if (!normalizedKeyword) {
        this.eventOptions = [];
        this.eventSearchKeyword = '';
        this.eventSearchTotal = 0;
        return;
      }
      this.eventSearchKeyword = normalizedKeyword;
      this.eventSearchPageNum = 1;
      this.eventSearchTotal = 0;
      this.eventOptions = [];
      this.loadEventOptions(true);
    },
    loadEventOptions(reset) {
      if (!this.eventSearchKeyword || this.eventSearchLoading || this.eventSearchLoadingMore) return;
      if (!reset && this.eventOptions.length >= this.eventSearchTotal) return;
      const requestId = ++this.eventSearchRequestId;
      const scrollTop = this.eventSearchScrollElement ? this.eventSearchScrollElement.scrollTop : 0;
      if (reset) {
        this.eventSearchLoading = true;
      } else {
        this.eventSearchLoadingMore = true;
      }
      this.$axios
        .get('/form-result/search_events', {
          params: {
            keyword: this.eventSearchKeyword,
            pageNum: this.eventSearchPageNum,
            pageSize: this.eventSearchPageSize
          }
        })
        .then((res) => {
          if (requestId !== this.eventSearchRequestId) return;
          const list = _.get(res, 'data.res.list') || [];
          this.eventSearchTotal = _.get(res, 'data.res.total') || 0;
          if (reset) {
            this.eventOptions = list;
          } else {
            const existingIds = new Set(this.eventOptions.map(event => event._id));
            this.eventOptions = this.eventOptions.concat(
              list.filter(event => !existingIds.has(event._id))
            );
          }
          this.$nextTick(() => {
            this.attachEventSearchScroll();
            if (!reset && this.eventSearchScrollElement) {
              this.eventSearchScrollElement.scrollTop = scrollTop;
            }
          });
        })
        .catch((err) => {
          if (requestId !== this.eventSearchRequestId) return;
          if (reset) {
            this.eventOptions = [];
          }
          this.$message.error(err.response?.data?.message || '赛事搜索失败');
        })
        .finally(() => {
          if (requestId === this.eventSearchRequestId) {
            this.eventSearchLoading = false;
            this.eventSearchLoadingMore = false;
          }
        });
    },
    handleEventDropdownVisible(visible) {
      if (visible) {
        this.$nextTick(this.attachEventSearchScroll);
      } else {
        this.detachEventSearchScroll();
      }
    },
    attachEventSearchScroll() {
      this.detachEventSearchScroll();
      const scrollElement = document.querySelector(
        '.event-search-popper .el-select-dropdown__wrap'
      );
      if (!scrollElement) return;
      this.eventSearchScrollElement = scrollElement;
      scrollElement.addEventListener('scroll', this.handleEventSearchScroll);
    },
    detachEventSearchScroll() {
      if (!this.eventSearchScrollElement) return;
      this.eventSearchScrollElement.removeEventListener('scroll', this.handleEventSearchScroll);
      this.eventSearchScrollElement = null;
    },
    handleEventSearchScroll(event) {
      const element = event.target;
      const reachedBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 24;
      if (!reachedBottom || this.eventSearchLoading || this.eventOptions.length >= this.eventSearchTotal) {
        return;
      }
      this.eventSearchPageNum += 1;
      this.loadEventOptions(false);
    },
    handleEventSelectionChange(eventId) {
      if (eventId && eventId !== '__event_loading_more__') {
        this.handleQuery();
      }
    },
    handleEventSelectionClear() {
      this.query = '';
      this.pageNum = 1;
      this.getData();
    },
    handleSearchModeChange() {
      this.pageNum = 1;
      this.paidCount = null;
      this.clearLocator();
      if (this.searchMode === 'event') {
        if (this.query) {
          this.handleQuery();
        }
      } else if (this.personQuery) {
        this.searchRegistrations(true);
      }
    },
    handlePersonSearchClear() {
      this.personQuery = '';
      this.pageNum = 1;
      this.paidCount = null;
      this.tableData = [];
      this.total = 0;
      this.clearLocator();
    },
    searchRegistrations(resetPage) {
      const keyword = String(this.personQuery || '').trim();
      if (!keyword) {
        this.$message.warning('请输入运动员姓名或手机号');
        return;
      }
      if (resetPage) {
        this.pageNum = 1;
      }
      const isPhone = /^\d+$/.test(keyword);
      this.personSearchLoading = true;
      this.$axios
        .post('/form-result/search_registrations', {
          name: isPhone ? '' : keyword,
          phone: isPhone ? keyword : '',
          onlyPaid: this.onlyPaid,
          pageNum: this.pageNum,
          pageSize: this.pageSize
        })
        .then((res) => {
          this.tableData = _.get(res, 'data.res.list') || [];
          this.total = _.get(res, 'data.res.total') || 0;
          this.paidCount = null;
          this.refreshLocatorMatches();
        })
        .catch((err) => {
          this.tableData = [];
          this.total = 0;
          this.$message.error(err.response?.data?.message || '报名记录查询失败');
        })
        .finally(() => {
          this.personSearchLoading = false;
        });
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
      this.$nextTick(() => {
        if (this.$refs.formResultTable && this.$refs.formResultTable.doLayout) {
          this.$refs.formResultTable.doLayout();
        }
      });
    },
    getIdTypeName(idType) {
      const typeMap = {
        'id_card': '身份证',
        'hk_pass': '来往内地通行证',
        'macao_pass': '来往内地通行证',
        'taiwan_pass': '来往大陆通行证',
        'hk_macao_residence_permit': '港澳居住证',
        'taiwan_residence_permit': '台湾居住证',
        'passport': '护照',
        'foreign_residence_permit': '外国人居留证'
      };
      return typeMap[idType] || '身份证';
    },
    getProfitSharingReturnStatusText(status) {
      const statusMap = {
        none: '未回退',
        processing: '处理中',
        success: '已回退',
        failed: '失败'
      };
      return statusMap[status || 'none'] || '未知';
    },
    getProfitSharingReturnStatusType(status) {
      const typeMap = {
        none: 'info',
        processing: 'warning',
        success: 'success',
        failed: 'danger'
      };
      return typeMap[status || 'none'] || 'info';
    },
    getProfitSharingReturnRemaining(row) {
      return Number(row.platformFee || 0) - Number(row.profitSharingReturnedAmount || 0);
    },
    canReturnProfitSharing(row) {
      return this.canReturnProfitSharingPermission &&
        row.paymentMode === 'service_provider' &&
        row.isPaid &&
        row.isProfitSharing &&
        row.profitSharingStatus === 'success' &&
        Number(row.platformFee || 0) > 0 &&
        this.getProfitSharingReturnRemaining(row) > 0;
    },
    handleLocatorShortcut(event) {
      const isFindShortcut = (event.metaKey || event.ctrlKey) && event.key && event.key.toLowerCase() === 'f';
      if (!isFindShortcut || !this.tableData.length) return;
      event.preventDefault();
      this.$nextTick(() => {
        if (this.$refs.locatorInput && this.$refs.locatorInput.focus) {
          this.$refs.locatorInput.focus();
        }
      });
    },
    getTableRowClassName({ row }) {
      return row && row._id === this.locatorActiveRowId ? 'locator-active-row' : '';
    },
    normalizeLocatorValue(value) {
      if (value === undefined || value === null) return '';
      if (Array.isArray(value)) {
        return value.map(item => this.normalizeLocatorValue(item)).join(' ');
      }
      if (typeof value === 'object') {
        return Object.keys(value).map(key => this.normalizeLocatorValue(value[key])).join(' ');
      }
      return String(value);
    },
    getLocatorRowText(row) {
      return [
        row._id,
        row.eventId,
        row.eventName,
        row.name,
        row.phone,
        row.wechat,
        row.orderNo,
        row.outTradeNo,
        row.transactionId,
        row.couponCode,
        row.couponName,
        row.school,
        row.grade,
        row.remark,
        row.team,
        this.normalizeLocatorValue(row.items),
        this.normalizeLocatorValue(row.feeItems)
      ].map(item => this.normalizeLocatorValue(item)).join(' ').toLowerCase();
    },
    refreshLocatorMatches() {
      const keyword = this.locatorKeyword.trim().toLowerCase();
      if (!keyword) {
        this.locatorMatches = [];
        this.locatorActiveIndex = -1;
        this.locatorActiveRowId = '';
        return;
      }

      this.locatorMatches = this.tableData.reduce((list, row, index) => {
        if (this.getLocatorRowText(row).includes(keyword)) {
          list.push(index);
        }
        return list;
      }, []);
      this.locatorActiveIndex = this.locatorMatches.length ? 0 : -1;
      this.updateActiveLocateRow();
    },
    handleLocatorInput() {
      this.refreshLocatorMatches();
    },
    clearLocator() {
      this.locatorKeyword = '';
      this.refreshLocatorMatches();
    },
    goPrevLocateMatch() {
      if (!this.locatorMatches.length) return;
      this.locatorActiveIndex = (this.locatorActiveIndex - 1 + this.locatorMatches.length) % this.locatorMatches.length;
      this.updateActiveLocateRow();
    },
    goNextLocateMatch() {
      if (!this.locatorMatches.length) return;
      this.locatorActiveIndex = (this.locatorActiveIndex + 1) % this.locatorMatches.length;
      this.updateActiveLocateRow();
    },
    updateActiveLocateRow() {
      if (this.locatorActiveIndex < 0 || !this.locatorMatches.length) {
        this.locatorActiveRowId = '';
        return;
      }

      const rowIndex = this.locatorMatches[this.locatorActiveIndex];
      const row = this.tableData[rowIndex];
      this.locatorActiveRowId = row ? row._id : '';
      this.$nextTick(() => {
        this.scrollToLocateRow(rowIndex);
      });
    },
    scrollToLocateRow(rowIndex) {
      const table = this.$refs.formResultTable && this.$refs.formResultTable.$el;
      if (!table) return;
      const bodyWrapper = table.querySelector('.el-table__body-wrapper');
      const rows = table.querySelectorAll('.el-table__body-wrapper tbody .el-table__row');
      const rowEl = rows[rowIndex];
      if (!bodyWrapper || !rowEl) return;

      bodyWrapper.scrollTop = Math.max(rowEl.offsetTop - 64, 0);
      rowEl.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: 'smooth'
      });
    },
    refreshList() {
      if (this.searchMode === 'person') {
        this.searchRegistrations(false);
      } else if (this.query) {
        this.handleQuery();
      } else {
        this.getData();
      }
    },
    handleProfitSharingReturn(row) {
      const remainingAmount = this.getProfitSharingReturnRemaining(row);
      const amountText = `${this.divide(remainingAmount, 100)} 元`;
      this.$confirm(
        `确认退回订单 ${row.orderNo} 的平台分账金额 ${amountText}？此操作只退回分账，不会发起退款。`,
        '分账回退确认',
        {
          confirmButtonText: '确认回退',
          cancelButtonText: '取消',
          type: 'warning'
        }
      ).then(() => {
        this.returningOrderNo = row.orderNo;
        this.$axios
          .post('/form-result/request_profit_sharing_return', {
            orderNo: row.orderNo
          }, {
            headers: {
              'x-auth-token': localStorage.getItem('bde_token')
            }
          })
          .then((res) => {
            const cloudResult = _.get(res, 'data.res.cloudResult');
            if (cloudResult && cloudResult.code && cloudResult.code !== 200) {
              this.$message.warning(cloudResult.msg || '分账回退已提交，但微信返回非成功状态');
            } else {
              this.$message.success(_.get(cloudResult, 'msg') || '分账回退已提交');
            }
            this.refreshList();
          })
          .catch((err) => {
            this.$message.error(err.response?.data?.message || '分账回退失败');
            if (err.response?.data?.code === 'token_timeout') {
              this.$router.push('/login');
            }
          })
          .finally(() => {
            this.returningOrderNo = '';
          });
      }).catch(() => {
        this.$message({
          type: 'info',
          message: '已取消分账回退'
        });
      });
    },
    handleEdit(row) {
      // 深拷贝当前行数据到编辑表单
      this.editForm = {
        _id: row._id,
        name: row.name,
        phone: row.phone,
        wechat: row.wechat,
        items: _.cloneDeep(row.items) || [{ item: '', group: '', price: 0 }],
        team: row.team || '',
        isProfitSharing: row.isProfitSharing || false,
        profitSharingStatus: row.profitSharingStatus || 'none',
        remark: row.remark || ''
      };
      this.editDialogVisible = true;
      // 清空表单验证
      this.$nextTick(() => {
        this.$refs.editForm.clearValidate();
      });
    },
    addItem() {
      this.editForm.items.push({ item: '', group: '', price: 0 });
    },
    removeItem(index) {
      if (this.editForm.items.length > 1) {
        this.editForm.items.splice(index, 1);
      }
    },
    handleSubmitEdit() {
      this.$refs.editForm.validate((valid) => {
        if (valid) {
          // 验证 items 数组中每个项目的 item 和 group 都不为空
          const hasEmptyItem = this.editForm.items.some(item => {
            return !item.item || !item.item.trim() || !item.group || !item.group.trim();
          });

          if (hasEmptyItem) {
            this.$message.error('请填写完整的项目名称和组别');
            return;
          }

          this.submitting = true;
          const updateData = {
            id: this.editForm._id,
            name: this.editForm.name.trim(),
            phone: this.editForm.phone,
            wechat: this.editForm.wechat.trim(),
            items: this.editForm.items.map(item => ({
              item: item.item.trim(),
              group: item.group.trim(),
              price: item.price // 保持原 price 不变
            })),
            team: this.editForm.team.trim(),
            isProfitSharing: this.editForm.isProfitSharing,
            profitSharingStatus: this.editForm.profitSharingStatus,
            remark: this.editForm.remark.trim()
          };

          this.$axios
            .post('/form-result/update', JSON.stringify(updateData), {
              headers: {
                'x-auth-token': localStorage.getItem('bde_token'),
                'Content-Type': 'application/json'
              }
            })
            .then((res) => {
              if (res.data.code === 'success') {
                this.$message.success('修改成功');
                this.editDialogVisible = false;
                this.refreshList();
              }
            })
            .catch((err) => {
              this.$message.error(err.response?.data?.message || '修改失败');
              if (err.response?.data?.code === 'token_timeout') {
                this.$router.push('/login');
              }
            })
            .finally(() => {
              this.submitting = false;
            });
        } else {
          this.$message.error('请填写完整信息');
          return false;
        }
      });
    },
    getData() {
      this.paidCount = null;
      this.$axios
        .get(`/form-result/find_all?pageSize=${this.pageSize}&pageNum=${this.pageNum}&onlyPaid=${this.onlyPaid}`, {
            headers: {
              "x-auth-token": localStorage.getItem("bde_token"),
            },
        })
        .then((res) => {
          this.tableData = _.get(res, 'data.res.list') || []
          this.total = _.get(res, 'data.res.total') || 0
          this.refreshLocatorMatches()
        })
        .catch((err) => {
          console.log(err);
        });
    },
    onCurrentPageChange(index) {
      this.pageNum = index
      if (this.searchMode === 'person') {
        this.searchRegistrations(false)
      } else {
        this.getData()
      }
    },
    handleOnlyPaidChange() {
      this.pageNum = 1
      if (this.searchMode === 'person') {
        if (this.personQuery) {
          this.searchRegistrations(true)
        }
      } else if (this.query) {
        this.handleQuery()
      } else {
        this.getData()
      }
    },
    handleDelete(_id) {
      this.$confirm("此操作将永久删除该条信息, 是否继续?", "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(() => {
          this.requestDelete(_id);
        })
        .catch(() => {
          this.$message({
            type: "info",
            message: "已取消删除",
          });
        });
    },
    requestDelete(id) {
      this.$axios.post("/form-result/delete",
          JSON.stringify({ id }),
          {
            headers: {
              "x-auth-token": localStorage.getItem("bde_token"),
            },
          })
        .then((res) => {
          if (res.data.code === "success") {
            this.$message.success("删除成功");
            this.refreshList();
          }
        })
        .catch((err) => {
          this.$message.error(err.response.data.message);
          if(err.response.data.code === 'token_timeout') {
            this.$router.push('/login')
          }
        });
    },
    handleQuery() {
      if (!this.query) return this.$message.error("请输入赛事ID！");
      this.$axios
        .post("/form-result/find_by_id", { id: this.query, onlyPaid: this.onlyPaid })
        .then((res) => {
          this.tableData = res.data.res || [];
          // 计算报名成功人数（isPaid 为 true 的记录数）
          this.paidCount = this.tableData.filter(item => item.isPaid).length;
          this.refreshLocatorMatches()
        })
        .catch((err) => {
          this.$message.error(err.response.data.message);
          console.log(err);
        });
    },
    exportExcel() {
      const eventId = String(this.query || '').trim();
      if (!eventId) {
        this.$message.error('请先输入赛事ID并查询后再导出');
        return;
      }
      if (!this.tableData.length) {
        this.$message.error('暂无可导出的报名名单');
        return;
      }
      this.exporting = true;
      this.$axios
        .get('/form-result/export_excel', {
          params: {
            id: eventId,
            validateOnly: true
          },
          headers: {
            "x-auth-token": localStorage.getItem("bde_token"),
          }
        })
        .then(() => {
          exportRegistrationRows(this.tableData, this.tableData[0], this.$dayjs);
        })
        .catch((err) => {
          this.$message.error(err.response?.data?.message || '导出报名名单失败');
          if (err.response?.data?.code === 'token_timeout') {
            this.$router.push('/login');
          }
        })
        .finally(() => {
          this.exporting = false;
        });
    },
    getExportFileName(eventData = {}) {
      return getRegistrationExportFileName(eventData, this.$dayjs);
    },
    formatBeijingDateCompact(date) {
      return formatBeijingDateCompact(date);
    },
    sanitizeFileName(name) {
      return sanitizeRegistrationFileName(name);
    },
    saveAsExcelFile(buffer, fileName) {
      saveAsExcelFile(buffer, fileName);
    },
    showProfitSharingResult(result) {
      this.profitSharingResultText = JSON.stringify(result, null, 2);
      this.profitSharingDialogVisible = true;
    },
    copyToClipboard() {
      const textarea = document.createElement('textarea');
      textarea.value = this.profitSharingResultText;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        this.$message.success('已复制到剪贴板');
      } catch (err) {
        this.$message.error('复制失败');
      }
      document.body.removeChild(textarea);
    }
  },
};
</script>

<style scoped>
.addon-dialog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
  color: #606266;
}

.addon-dialog-toolbar ::v-deep .addon-export-button.el-button--primary.is-plain:not(.is-disabled) {
  color: var(--brand-primary);
  background-color: var(--brand-primary-lighter);
  border-color: var(--brand-primary-light);
}

.addon-dialog-toolbar ::v-deep .addon-export-button.el-button--primary.is-plain:not(.is-disabled):hover,
.addon-dialog-toolbar ::v-deep .addon-export-button.el-button--primary.is-plain:not(.is-disabled):focus {
  color: #ffffff;
  background-color: var(--brand-primary);
  border-color: var(--brand-primary);
}

.filter-tools {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 40px;
}
.search-mode-switch {
  display: inline-flex;
  flex: 0 0 auto;
  white-space: nowrap;
}
.search-mode-switch ::v-deep .el-radio-button__inner {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.event-search-select {
  width: min(520px, 42vw);
}
.person-search-input {
  width: min(360px, 36vw);
}
.filter-tools .el-button.is-disabled,
.filter-tools .el-button.is-disabled:hover,
.filter-tools .el-button.is-disabled:focus {
  color: var(--text-placeholder);
  background: var(--bg-table-header);
  border-color: var(--border-base);
  box-shadow: none;
}
.event-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
}
.event-option-name {
  overflow: hidden;
  color: var(--text-primary);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.event-option-meta {
  flex: 0 0 auto;
  color: var(--text-secondary);
  font-size: 12px;
}
.event-option-loading {
  color: var(--text-secondary);
  font-size: 13px;
  text-align: center;
}
.filter-switch {
  display: flex;
  align-items: center;
  margin-left: 10px;
}
.switch-label {
  margin-right: 10px;
  font-size: 14px;
  color: #606266;
}
.pager {
  margin-top: 20px;
}
.color-male {
  color: #409EFF;
}
.color-male-female {
  color: #F56C6C;
}
.btn-export {
  margin-left: 10px;
  height: 40px;
  padding: 0 16px;
  font-size: 14px;
}
.export-button-wrap {
  display: inline-flex;
}
.export-button-wrap .btn-export.is-disabled,
.export-button-wrap .btn-export.is-disabled:hover,
.export-button-wrap .btn-export.is-disabled:focus {
  color: #8b95a5;
  background: #eef1f5;
  border-color: #d8dee8;
  box-shadow: none;
}
.paid-count {
  margin-left: 10px;
}
.page-locator {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: -20px 0 16px;
}
.locator-input {
  width: 360px;
}
.locator-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.locator-count {
  min-width: 48px;
  color: #606266;
  font-size: 13px;
  text-align: center;
}
.form-result-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.form-result-actions .el-button {
  margin: 0;
}

.lark-notification-cell {
  display: flex;
  min-height: 62px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 6px 0;
}

.lark-status-row,
.lark-notification-meta {
  display: flex;
  align-items: center;
  justify-content: center;
}

.lark-status-row {
  gap: 6px;
}

.lark-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  padding: 2px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  white-space: nowrap;
}

.lark-status-pill.is-untracked {
  color: var(--text-secondary);
  background: var(--bg-table-header);
  border-color: var(--border-base);
}

.lark-status-pill.is-pending,
.lark-status-pill.is-sending {
  color: var(--color-warning);
  background: rgba(230, 162, 60, 0.12);
  border-color: rgba(230, 162, 60, 0.28);
}

.lark-status-pill.is-retrying {
  color: var(--color-danger);
  background: rgba(245, 108, 108, 0.12);
  border-color: rgba(245, 108, 108, 0.28);
}

.lark-status-pill.is-sent {
  color: var(--color-success);
  background: rgba(103, 194, 58, 0.12);
  border-color: rgba(103, 194, 58, 0.28);
}

.lark-returning-badge {
  padding: 2px 6px;
  color: var(--color-success);
  background: rgba(103, 194, 58, 0.1);
  border-radius: 4px;
  font-size: 11px;
  line-height: 18px;
  white-space: nowrap;
}

.lark-notification-meta {
  gap: 4px;
  color: var(--text-secondary);
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

.lark-meta-dot {
  color: var(--text-placeholder);
}

.lark-error-icon {
  margin-left: 2px;
  color: var(--color-danger);
  cursor: help;
}

.lark-retry-button {
  min-width: 88px;
  margin: 0 !important;
  padding: 6px 12px !important;
}
::v-deep .locator-active-row > td {
  background-color: #fff7d6 !important;
}
::v-deep .locator-active-row:hover > td {
  background-color: #fff0b3 !important;
}
.json-viewer {
  background-color: #f5f5f5;
  padding: 20px;
  border-radius: 4px;
  overflow: auto;
  max-height: 500px;
  font-family: 'Courier New', Courier, monospace;
  font-size: 13px;
  line-height: 1.5;
  color: #333;
  border: 1px solid #e0e0e0;
}
.item-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.item-input {
  flex: 1;
}
.item-price {
  width: 120px;
}
@media (max-width: 768px) {
  .filter-tools {
    gap: 10px;
    margin-bottom: 20px;
  }

  .event-search-select {
    width: 100%;
    margin-bottom: 0;
  }

  .search-mode-switch,
  .person-search-input {
    width: 100%;
  }

  .search-mode-switch {
    display: flex;
    flex-wrap: nowrap;
    gap: 0;
  }

  .search-mode-switch ::v-deep .el-radio-button {
    display: block;
    flex: 1 1 0;
    width: 50%;
    min-width: 0;
  }

  .search-mode-switch ::v-deep .el-radio-button__inner {
    width: 100%;
    height: 40px;
    padding-right: 8px;
    padding-left: 8px;
    font-size: 12px;
    white-space: nowrap;
  }

  .search-mode-switch ::v-deep .el-radio-button:first-child .el-radio-button__inner {
    border-radius: 4px 0 0 4px;
  }

  .search-mode-switch ::v-deep .el-radio-button:last-child .el-radio-button__inner {
    border-radius: 0 4px 4px 0;
  }

  .search-mode-switch ::v-deep .el-radio-button:not(:first-child) .el-radio-button__inner {
    margin-left: -1px;
  }

  .search-mode-switch ::v-deep .el-radio-button__orig-radio:checked + .el-radio-button__inner {
    position: relative;
    z-index: 1;
  }

  .filter-switch {
    flex: 1;
    width: auto !important;
    min-width: 0;
    margin-left: 0;
  }

  .btn-export {
    width: 88px;
    height: 40px;
    margin-left: 0;
    padding: 0 12px;
  }

  .export-button-wrap {
    width: 88px;
  }

  .paid-count {
    width: 100%;
    margin-left: 0;
  }

  .page-locator {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    margin: 0 0 12px;
  }

  .locator-input {
    width: 100%;
  }

  .locator-actions {
    justify-content: space-between;
  }

  .form-result-actions {
    flex-direction: column;
    align-items: center;
  }

  .form-result-actions .el-button {
    width: 40px;
    min-width: 40px;
    height: 36px;
    padding: 0;
  }

  .form-result-actions .el-button [class*="el-icon-"] + span {
    margin-left: 0;
  }

  .profit-return-text {
    display: none;
  }
}
</style>

<style>
.event-search-popper {
  width: min(520px, 42vw) !important;
  max-width: calc(100vw - 24px);
}

@media (max-width: 768px) {
  .event-search-popper {
    width: calc(100vw - 20px) !important;
  }
}
</style>
